import { useEffect, useState, useRef } from 'react';

/**
 * Custom hook for handling state transitions with animations
 * @param currentState The current state that will trigger animations when changed
 * @param animationDuration Duration of each animation phase in milliseconds
 * @returns Animation state information and helper functions
 */
export function useTransitionAnimation<T>(
  currentState: T,
  animationDuration = 300,
  ignoredTransitions: [T, T][] = [] // Add parameter for transitions to ignore
) {
  const [visibleState, setVisibleState] = useState<T>(currentState);
  
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  
  const nextStateRef = useRef<T | null>(null);
  
  useEffect(() => {
    if (currentState !== visibleState) {
      // Check if this transition should be ignored
      const shouldIgnore = ignoredTransitions.some(
        ([from, to]) => 
          (visibleState === from && currentState === to) || 
          (visibleState === to && currentState === from)
      );
      
      if (shouldIgnore) {
        // Immediately update visibleState without animation
        setVisibleState(currentState);
        return;
      }
      
      // Normal animation flow
      nextStateRef.current = currentState;
      setIsAnimating(true);
      setDirection('out');
    }
  }, [currentState, visibleState, ignoredTransitions]);

  // Rest of the hook remains the same
  useEffect(() => {
    if (direction === 'out' && isAnimating && nextStateRef.current !== null) {
      const timer = setTimeout(() => {
        setVisibleState(nextStateRef.current as T);
        setDirection('in');
      }, animationDuration);
      
      return () => clearTimeout(timer);
    }
  }, [direction, isAnimating, animationDuration]);
  
  useEffect(() => {
    if (direction === 'in' && isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
        nextStateRef.current = null;
      }, animationDuration);
      
      return () => clearTimeout(timer);
    }
  }, [direction, isAnimating, animationDuration]);
  
  const getAnimationClass = () => {
    if (!isAnimating) return 'translate-y-0 opacity-100';
    if (direction === 'out') return 'translate-y-8 opacity-0';
    return 'translate-y-0 opacity-100';
  };
  
  return {
    visibleState,
    isAnimating,
    direction,
    getAnimationClass
  };
}